import React from "react";
// material-ui components
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Slide from "@material-ui/core/Slide";
import Dialog from "@material-ui/core/Dialog";
import Grid from "@material-ui/core/Grid";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
// @material-ui/icons
import Close from "@material-ui/icons/Close";
// core components
import Button from "components/CustomButtons/Button";

import SweetAlert from "react-bootstrap-sweetalert";

import * as Yup from "yup";
import axios from "axios";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { withRouter } from "react-router-dom";
import { Divider, Typography } from "@material-ui/core";

import { useDebouncedCallback } from "use-debounce";
import LoadingModal from "./CreateClientLoading";
import alertStyles from "assets/jss/elite-feats-cms/views/sweetAlertStyle";
import { useTranslation } from "react-i18next";

const styles = { ...alertStyles };

const useStyles = makeStyles(styles);

const TField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "#63ac1d",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#3c9778",
    },
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#63ac1d",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#63ac1d",
      },
    },
  },
})(TextField);

interface CreateClientProps {
  openCreateClient: boolean;
  setClientsChanged: (value: number) => void;
  setOpenCreateClient: (open: boolean) => void;
  setTriggerStep?: (step: number) => void;
  tourRun?: boolean;
}

export default function Modal(props: CreateClientProps): React.ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();
  const [alert, setAlert] = React.useState<React.ReactElement | null>(null);
  const [initialValues, setInitial] = React.useState({
    first: "",
    last: "",
    email: "",
  });
  const [open, setOpen] = React.useState(false);
  const hideAlert = (loc?: string) => {
    if (loc === "success") {
      setAlert(null);

      setInitial({ first: "", last: "", email: "" });
    }
    setAlert(null);
  };

  const dangerAlert = () => {
    setAlert(
      <SweetAlert
        danger
        style={{ display: "block", marginTop: "-100px" }}
        title={t("clients:clientNotAdded")}
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnCssClass={classes.button + " " + classes.danger}
      >
        {t("clients:clientExistsError")}
      </SweetAlert>
    );
  };

  const successAlert = () => {
    setAlert(
      <SweetAlert
        success
        style={{ display: "block", marginTop: "-100px" }}
        title={t("clients:clientAdded")}
        onConfirm={() => hideAlert("success")}
        onCancel={() => hideAlert("success")}
        confirmBtnCssClass={classes.button + " " + classes.success}
      >
        {t("clients:clientListUpdated")}
      </SweetAlert>
    );
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("clients:invalidEmailFormat"))
      .required(t("clients:enterValidEmail")),
    first: Yup.string()
      .min(2, t("clients:minTwoChars"))
      .required(t("clients:enterFirstName")),
    last: Yup.string()
      .min(2, t("clients:minTwoChars"))
      .required(t("clients:enterLastName")),
  });

  const handleSubmit = useDebouncedCallback(
    (values: { first: string; last: string; email: string }) => {
    props.setOpenCreateClient(false);
    setOpen(true);

    axios
      .post("/api/client/create-client", values, {
        headers: {
          "auth-token": localStorage.getItem("token"),
        },
      })
      .then((res) => {
        setOpen(false);
        if (res.data === "client exists") {
          if (props.tourRun) {
            props.setTriggerStep!(-2);
          }
          dangerAlert();
        } else if (res.data === "added") {
          if (props.tourRun) {
            props.setClientsChanged(Math.random());
            setTimeout(() => {
              props.setTriggerStep!(12);
            }, 1000);
          } else {
            successAlert();
            props.setClientsChanged(Math.random());
          }
        }
      });

    setInitial({
      first: "",
      last: "",
      email: "",
    });
  }, 500);

  return (
    <div>
      <Dialog
        classes={{
          root: classes.center,
          paper: classes.modal,
        }}
        open={props.openCreateClient}
        keepMounted
        disableBackdropClick
        disableEnforceFocus
        onClose={() => props.setOpenCreateClient(false)}
        aria-labelledby="modal-slide-title"
        aria-describedby="modal-slide-description"
      >
        <DialogTitle
          id="classic-modal-slide-title"
          disableTypography
          className={classes.modalHeader}
          style={{ paddingTop: 30 }}
        >
          <Typography style={{ fontWeight: 900, fontSize: 22 }}>
            {t("clients:createNewClient")}
          </Typography>
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            handleSubmit(values);
            // @ts-expect-error preexisting Formik misuse (latent bug, preserved per types-only)
        resetForm(initialValues);
            setSubmitting(false);
          }}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form style={{ maxWidth: 350 }}>
              <DialogContent
                id="modal-slide-description"
                className={classes.modalBody}
              >
                <Field
                  className="first-name"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  margin="normal"
                  required
                  as={TField}
                  id="first"
                  label={t("clients:firstName")}
                  name="first"
                  autoFocus
                  fullWidth
                  helperText={
                    <ErrorMessage
                      component="div"
                      style={{ color: "#c5473f" }}
                      name="first"
                    />
                  }
                />

                <Field
                  className="last-name"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  margin="normal"
                  required
                  as={TField}
                  fullWidth
                  name="last"
                  label={t("clients:lastName")}
                  id="last"
                  helperText={
                    <ErrorMessage
                      component="div"
                      style={{ color: "#c5473f" }}
                      name="last"
                    />
                  }
                />

                <Field
                  className="client-email"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  margin="normal"
                  required
                  autoComplete="email"
                  as={TField}
                  id="email"
                  label={t("clients:emailAddress")}
                  name="email"
                  autoFocus
                  fullWidth
                  helperText={
                    <ErrorMessage
                      component="div"
                      style={{ color: "#c5473f" }}
                      name="email"
                    />
                  }
                />

                <Grid
                  style={{
                    marginBottom: "3em",
                    display: "flex",
                    justifyContent: "center",
                  }}
                ></Grid>
              </DialogContent>
              <Divider light style={{ marginBottom: 10 }} />
              <DialogActions
                className={
                  classes.modalFooter + " " + classes.modalFooterCenter
                }
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingBottom: 20,
                }}
              >
                <Button
                  round
                  style={{ marginRight: 10 }}
                  onClick={() => {
                    props.setOpenCreateClient(false);
                  }}
                >
                  {t("clients:cancel")}
                </Button>
                <Button
                  className="client-create"
                  round
                  style={{ marginLeft: 10 }}
                  type="submit"
                  color="success"
                  onClick={() => {
                    if (props.tourRun) {
                      props.setTriggerStep!(-2);
                    }
                  }}
                >
                  {t("clients:create")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <LoadingModal open={open} setOpen={setOpen} />
      {alert}
    </div>
  );
}
